import { useLocation } from 'react-router';
import equal from 'fast-deep-equal';
import type { FetcherState, FilterType, QueryParams, TableUrlSync } from './types';
import { getStoredState, setStoredState } from './storage';
import { serializeQs } from './useQueryState';

type FetcherParams = {
  pageSize?: number;
  defaultPageSize?: number;
  queryParams?: QueryParams;
  defaultFilter?: FilterType;
  defaultSort?: FetcherState['sort'];
  storageKey?: string;
  urlSync?: TableUrlSync;
};

type Fetcher = {
  initialState: FetcherState;
  onStateChange?: (state: FetcherState) => void;
  defaultFilter: FilterType;
  navigateTo?: string;
};

const defaultDefualtFilter = {};

export function useFetcher(params: FetcherParams): Fetcher {
  const {
    pageSize: constantPageSize,
    defaultPageSize = 15,
    defaultFilter = defaultDefualtFilter,
    defaultSort,
    storageKey,
    queryParams,
    urlSync = 'push',
  } = params;

  const baseInitialState: FetcherState = {
    pageIndex: 1,
    pageSize: constantPageSize || defaultPageSize,
    filter: defaultFilter,
    sort: defaultSort,
    queryParams,
  };

  const location = useLocation();
  let initialState = baseInitialState;
  let navigateTo;

  if (storageKey && urlSync === null) {
    const restoredState = getStoredState(storageKey);
    if (restoredState) {
      initialState = {
        ...baseInitialState,
        ...restoredState,
        filter: {
          ...baseInitialState.filter,
          ...restoredState.filter,
        },
      };
    }
  } else if (storageKey && !location.search) {
    // Restore saved filters whenever we land on a bare index URL (no
    // `location.search`). This is recomputed on every render while we're on
    // the bare URL so that the `<Navigate>` we return stays mounted until its
    // effect actually runs — a latch here would let an intermediate re-render
    // unmount `<Navigate>` before it navigates, dropping the filters. Once the
    // redirect happens, `location.search` is populated and this branch stops.
    const restoredState = getStoredState(storageKey);
    if (restoredState) {
      navigateTo = `${location.pathname}${serializeQs({
        ...baseInitialState,
        ...restoredState,
        filter: {
          ...baseInitialState.filter,
          ...restoredState?.filter,
        },
      })}`;
    }
  }

  // write back to storage when state changes
  const onStateChange = storageKey
    ? (state: FetcherState) => setStoredState(storageKey, equal(state, baseInitialState) ? undefined : state)
    : undefined;

  return {
    initialState,
    defaultFilter,
    onStateChange,
    navigateTo,
  };
}
